import React, { useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Grid from '@mui/material/Grid2';
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CommonCard from '../../components/common_card';
import Button from "@mui/material/Button";
import { Stack } from '@mui/material';
import { TypographyBold, TypographyStepTitle, TypographyInstruction } from '../../style/typography';
import ReportTimeline from '../../components/arrowbox'
import ReportCountdown from '../../components/report_countdown'
import ReportStopDialog from '../../components/report_stop_dialog'
import { RequestButton } from "../../style/button";
import { BoxCenterStart } from '../../style/box'
import { BaseDialog } from '../../components/base_dialog';
import { REPORTING_TIME } from '../../const/config'
import { TaskStep } from '../../components/workflow_step'
import { TaskStepParam } from '../../const/task_step_param';
import { postData, fetchData } from "../../func/api";

const styles = {
    gridContainer: {
        pt: { xs: 2, sm: 3, md: 10 },
        pb: { xs: 1, sm: 3, md: 10 },
        height: '100%',
        width: { xs: '90%', sm: '60%', md: '50%' }
    },
    pageTitle: {
        textAlign: 'center',
        padding: '10px 0'
    },
}

const InitialTask = (props) => {
    const [earthquake, setEarthquake] = useState([]);
    const [taskStatus, setTaskStatus] = useState(() =>
        parseInt(localStorage.getItem("taskStatus")) || 0
    );
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { uuid } = useParams();
    const { currentUser } = useAuth();

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // データ取得
    useEffect(() => {
        if (!uuid) return;
        const fetchAll = async () => {
            try {
                const [earthquakeData] = await Promise.all([
                    fetchData(`api/get_earthquake/${uuid}`)
                ]);
                // 地震データ取得
                setEarthquake(earthquakeData);
            } catch (e) {
                console.error('fetch error:', e);
            }
        };
        fetchAll();
    }, [uuid]);

    const handleFirstReportQuick = useCallback(async () => {
        if (taskStatus === 12) {
            const firstTaskData = {
                status: taskStatus,
                is_finished: "TRUE",
                report_uuid: earthquake.report_uuid
            };
            await postData("api/post_update_first_task", firstTaskData);
            window.location.href = `/upload_csvfile/${uuid}`;
        } else {
            const newStatus = taskStatus + 1;
            setTaskStatus(newStatus)
            handleClose()

            // newStatus が 1 の場合、登録処理
            if (newStatus === 1) {
                const firstTaskData = {
                    report_uuid: earthquake.report_uuid,
                    table_name: "report_progress",
                    user_uuid: currentUser.id
                };
                await postData("api/post_insert_first_task", firstTaskData);
            }
            else {
                const firstTaskData = {
                    status: newStatus,
                    report_uuid: earthquake.report_uuid
                };
                await postData("api/post_update_first_task", firstTaskData);

                // newStatus が 2 または 3 または 4 または 5 の場合、更新処理
                if (newStatus === 2 || newStatus === 3 || newStatus === 4 || newStatus === 5) {
                    const reportProgressData = {
                        table_name: "report_progress",
                        column_name1:
                            newStatus === 2
                                ? "ft_checked_at"
                                : newStatus === 3
                                    ? "ft_reported_to_leader_at"
                                    : newStatus === 4
                                        ? "ft_reported_at"
                                        : newStatus === 5
                                            ? "ft_reported_to_internal_at"
                                            : "ir_reported_to_internal_at",
                        column_name2:
                            newStatus === 2
                                ? "ft_checked_by"
                                : newStatus === 3
                                    ? "ft_reported_to_leader_by"
                                    : newStatus === 4
                                        ? "ft_reported_by"
                                        : newStatus === 5
                                            ? "ft_reported_to_internal_by"
                                            : "ir_reported_to_internal_by",
                        earthquake: earthquake,
                        user_uuid: currentUser.id
                    };
                    await postData("api/post_update_report_progress", reportProgressData);
                }
            }
            window.history.pushState({ taskStatus: newStatus }, "");
            return
        }
    }, [taskStatus, earthquake, currentUser]);

    useEffect(() => {
        const handlePopState = (event) => {
            if (event.state && event.state.taskStatus !== undefined) {
                setTaskStatus((prevStatus) => {
                    const newStatus = Math.max(prevStatus - 1, 0);
                    localStorage.setItem("taskStatus", newStatus);
                    return newStatus;
                });
            } else {
                // 戻る履歴がない場合はデフォルトの状態に戻す
                setTaskStatus(0);
                localStorage.setItem("taskStatus", "0");
            }
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    useEffect(() => {
        localStorage.setItem("taskStatus", taskStatus);
    }, [taskStatus]);

    const currentTask = TaskStepParam[taskStatus]

    return (
        <>
            <BoxCenterStart pb={15}>
                <Grid container
                    spacing={5}
                    columns={12}
                    sx={styles.gridContainer}
                >
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <ReportStopDialog initial_report_uuid={earthquake.first_task_uuid} earthquake_uuid={earthquake.uuid} />
                        <CommonCard
                            child={
                                <TypographyBold sx={styles.pageTitle}>第1報速報および初期対応</TypographyBold>
                            }
                        >
                        </CommonCard>
                    </Grid>

                    {currentTask && (
                        <TaskStep
                            title={currentTask.title}
                            instructions={currentTask.instructions}
                            listItems={currentTask.listItems}
                            appealContent={currentTask.appealContent}
                            buttonText={currentTask.buttonText || '次へ'}
                            onNext={handleClickOpen}
                        />
                    )}

                    <BaseDialog
                        open={open}
                        onClose={handleClose}
                        title="業務実施確認"
                        message={`次の業務を実施します。\nよろしいですか？`}
                        onConfirm={handleFirstReportQuick}
                        confirmText='OK'
                        cancelText='キャンセル'
                    />
                    <ReportCountdown occued_at={new Date()} addedMinutes={REPORTING_TIME.INITIALTASK} />
                </Grid>
            </BoxCenterStart>
        </>
    )
}
export default InitialTask;